import { Controller } from 'stimulus';
import Rails from '@rails/ujs';
import Turbolinks from 'turbolinks';

// Answers are saved page by page, so an attempt survives the learner walking
// away from it. What does not survive is the page they are looking at, and the
// attempt stays unsubmitted until they reach the end, so leaving is worth a
// word either way.
export default class extends Controller {
  static values = {
    attemptStarted: Boolean,
    taskUrl: String,
    confirmTitle: String,
    confirmMessage: String,
    confirmCancel: String,
    confirmAllow: String
  };

  connect() {
    this._onBeforeUnload = this._onBeforeUnload.bind(this);
    this._onBeforeVisit = this._onBeforeVisit.bind(this);
    this._onBeforeLeave = this._onBeforeLeave.bind(this);

    window.addEventListener('beforeunload', this._onBeforeUnload);
    document.addEventListener('turbolinks:before-visit', this._onBeforeVisit);
    document.addEventListener('promote:before-leave-content', this._onBeforeLeave);
  }

  disconnect() {
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    document.removeEventListener('turbolinks:before-visit', this._onBeforeVisit);
    document.removeEventListener('promote:before-leave-content', this._onBeforeLeave);
  }

  _onBeforeUnload(event) {
    if (!this._shouldWarn()) return;

    event.preventDefault();
    event.returnValue = '';
  }

  _onBeforeVisit(event) {
    this._confirmLeaving(event, () => Turbolinks.visit(event.data.url));
  }

  // Going back to the task puts a snapshot of it back on screen, taken before
  // the attempt was opened, so the task has to be asked for again or it comes
  // back claiming nothing happened since.
  _onBeforeLeave(event) {
    const proceed = () => {
      event.detail.proceed();
      if (event.detail.restoresSnapshot) this._reloadTask();
    };

    if (this._shouldWarn()) {
      this._confirmLeaving(event, proceed);
      return;
    }

    if (!event.detail.restoresSnapshot) return;

    event.preventDefault();
    proceed();
  }

  // Rails.confirm renders the modal and answers through callbacks rather than
  // returning, so the original event has to be cancelled first and the way out
  // replayed once the learner confirms.
  _confirmLeaving(event, proceed) {
    if (!this._shouldWarn()) return;

    event.preventDefault();

    Rails.confirm(null, this.element, {
      confirmTitle: this.confirmTitleValue,
      confirmMessage: this.confirmMessageValue,
      confirmCancel: this.confirmCancelValue,
      confirmAllow: this.confirmAllowValue
    }, {
      confirmCallback: () => {
        this.disconnect();
        proceed();
      }
    });
  }

  _reloadTask() {
    if (!this.hasTaskUrlValue) return;

    Rails.ajax({ url: this.taskUrlValue, type: 'GET', dataType: 'script' });
  }

  _shouldWarn() {
    return this.attemptStartedValue || this._currentPageAnswered();
  }

  _currentPageAnswered() {
    const choices = this.element.querySelectorAll('input[type=radio], input[type=checkbox]');
    if (Array.from(choices).some((choice) => choice.checked)) return true;

    const textFields = this.element.querySelectorAll('input[type=text], textarea');
    return Array.from(textFields).some((field) => field.value.trim() !== '');
  }
}
